/*
 * Copyright (c) 2025 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, October 2nd 2025, 2:15:00 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { schemaBuilder } from "lib/validation";
import {
  ProcessTemplateArtifact,
  ProcessTemplateArtifactValidationSchema,
  ProcessTemplateArtifactSchemaType,
  FormConfiguration,
} from "lib/schemas";
import { WritableModel } from "lib/schemas/abstract";

export enum AIProviders {
  ollama = "Ollama",
  openai = "OpenAI",
}

export interface AIAgentConfiguration extends ProcessTemplateArtifact, WritableModel<true> {
  name: string;
  prompt: string;
  input?: string;
  model_identifier?: string;
  provider_code: keyof typeof AIProviders;
  max_tokens?: number;
  readonly created_at?: Date;
  readonly updated_at?: Date;
}

type WriteOnlyAIAgentConfiguration = Omit<AIAgentConfiguration, "id" | "created_at" | "updated_at">;

export const AIAgentConfigurationValidationSchema: ProcessTemplateArtifactSchemaType<WriteOnlyAIAgentConfiguration> =
  ProcessTemplateArtifactValidationSchema.shape({
    name: schemaBuilder.string().required("You must provide a name for the configuration."),
    prompt: schemaBuilder.string().required("You must provide instructions."),
    input: schemaBuilder.string().nullable().optional(),
    max_tokens: schemaBuilder.number().nullable().optional(),
    model_identifier: schemaBuilder.string().nullable(),
    provider_code: schemaBuilder.mixed().oneOf(Object.keys(AIProviders)).optional(),
  });

export interface AIFormReviewAgent extends WritableModel<true>, ProcessTemplateArtifact {
  ai_agent_configuration: AIAgentConfiguration["id"];
  ai_agent_configuration_object?: AIAgentConfiguration;
  form: FormConfiguration["id"];
  active?: boolean;
}

type WriteOnlyAIFormReviewAgent = Omit<AIFormReviewAgent, "id" | "active" | "ai_agent_configuration_object">;

export const AIFormReviewAgentValidationSchema: ProcessTemplateArtifactSchemaType<WriteOnlyAIFormReviewAgent> =
  ProcessTemplateArtifactValidationSchema.shape({
    ai_agent_configuration: schemaBuilder.string().required("Config required"),
    form: schemaBuilder.number().required("Form required"),
  });
